'use client';
import React from 'react';
import { Button, Text, Rate } from '@benzinga/core-ui';
import { SmartLink } from '@benzinga/analytics';
import Image from 'next/image';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import Hooks from '@benzinga/hooks';
import { CallToActionButton } from '@benzinga/ui';
import { WordpressPost } from '@benzinga/content-manager';
import styles from './ProductBasedCTA.module.scss';

export type ProductBasedCTALayout = 'aol' | 'advertorial' | 'compact_ads' | 'default' | 'stacked' | 'vertical';

export interface ProductBasedCTAProps {
  advertorial?: WordpressPost | null;
  button_text: string;
  description?: string;
  disable_disclosure?: boolean;
  highlight_list?: string[] | [];
  image_url?: string;
  layout?: ProductBasedCTALayout;
  location?: string;
  review?: number;
  review_url?: string;
  title: string;
  tooltip?: string;
  url: string;
}

export const ProductBasedCTA: React.FC<ProductBasedCTAProps> = ({
  button_text,
  description,
  highlight_list,
  image_url,
  layout,
  location = 'default',
  review,
  review_url,
  title,
  url,
}) => {
  const { width } = Hooks.useWindowSize();

  const variant = width < 800 || layout === 'vertical' ? 'vertical' : 'default';

  return (
    <div className={`${styles.campaignWrapper} call-to-action ${variant} ${layout}`}>
      <div className="call-to-action-container">
        <SmartLink action="click" category={location} href={url}>
          <div className="call-to-action-wrapper">
            <div className="promo-image-wrap">
              {image_url && (
                <div className="image-wrapper">
                  <div>
                    <Image
                      alt={title}
                      className="h-[75px] max-w-full object-contain"
                      height={75}
                      sizes="120px"
                      src={image_url}
                      width={120}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="call-to-action-copy">
              <div className="call-to-action-copy-content">
                <div>
                  <div className={styles.title}>{title}</div>
                  {description && (
                    <Text
                      className="call-to-action-description"
                      dangerouslySetInnerHTML={{ __html: sanitizeHTML(description) }}
                      lines={5}
                    />
                  )}
                  {highlight_list && highlight_list.length > 0 && (
                    <div className="call-to-action-highlight-list-wrapper">
                      <ul className="call-to-action-highlight-list">
                        {highlight_list.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="cta-review">
                  {(review && review > 0) || review_url ? (
                    <div className="item-review">
                      <Button className="w-full review-button">
                        <>
                          {review && review > 0 && (
                            <div className="review-number">
                              <span>{review}</span>/5
                            </div>
                          )}
                          <div className="review-detail">
                            {review && review > 0 && (
                              <Rate allowHalf className="rating-component" readOnly={true} value={review} />
                            )}
                            {review_url && (
                              <a className="w-full review-link" href={review_url}>
                                <span>Read Review</span>
                              </a>
                            )}
                          </div>
                        </>
                      </Button>
                    </div>
                  ) : null}
                  <CallToActionButton text={button_text} variant="flat-blue" />
                </div>
              </div>
            </div>
          </div>
        </SmartLink>
      </div>
    </div>
  );
};
